import {
  LightbulbIcon,
  ClipboardList,
  HelpCircle,
  Ruler,
  RefreshCw,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  BookmarkIcon,
} from "lucide-react";
import { tC } from "../../utils/TranslateWithContentLanguage";
import Markdown from "react-markdown";
import { useRef, useState, useEffect } from "react";
import CardImage from "../tool_components/CardImage";
import { Button, Text } from "@core/index";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { AiAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import AiAssignmentCardPDF from "./AiAssignmentCardPdf";
import i18n from "@packages/i18n/config";
import { motion, AnimatePresence } from "framer-motion";
import { MascotBubble } from "../tool_components/MascotBubble";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import { useAppSelector } from "@packages/store/hooks";
import {
  getGetAiAssignmentQueryKey,
  getGetUserAiAssignmentsQueryKey,
} from "@packages/api/madlenAPI";
import useIsAuthor from "@packages/hooks/useIsAuthor";
import RegeneratingOverlay from "../tool_components/RegenLoadingOverlay";

export interface AiAssignmentCardProps {
  assignment: AiAssignmentResponseDto;
  isImageLoading?: boolean;
  onCreateAgain?: () => void;
}

type TabType =
  | "studentInstructions"
  | "implementationSteps"
  | "reflectionQuestions"
  | "gradingRubric"
  | "aiPromptExamples";

const AiAssignmentCard = ({
  assignment,
  isImageLoading,
  onCreateAgain,
}: AiAssignmentCardProps) => {
  const finalImageUrl = useImagePdf(assignment.imageUrl);
  const contentLanguage = assignment.contentLanguage || "tr";
  const [activeTab, setActiveTab] = useState<TabType>("studentInstructions");
  const scrollRef = useRef<HTMLDivElement>(null);
  // Track expanded rubrics (expanded by default)
  const [expandedRubrics, setExpandedRubrics] = useState<number[]>([]);
  // Track copied example prompts
  const [copiedPrompts, setCopiedPrompts] = useState<Record<string, boolean>>(
    {},
  );
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  // Initialize all rubrics as expanded by default when rubric tab is active
  useEffect(() => {
    if (activeTab === "gradingRubric" && assignment.gradingRubric) {
      const allRubricIds = assignment.gradingRubric.map((_, index) => index);
      setExpandedRubrics(allRubricIds);
    }
  }, [activeTab, assignment.gradingRubric]);

  // Toggle rubric expansion
  const toggleRubric = (index: number) => {
    setExpandedRubrics((prev) =>
      prev.includes(index)
        ? prev.filter((id) => id !== index)
        : [...prev, index],
    );
  };

  // Define available tabs
  const tabs = [
    {
      id: "studentInstructions" as TabType,
      icon: ClipboardList,
      label: tC(contentLanguage, "aiAssignmentDesigner.studentInstructions"),
      count: assignment.studentInstructions ? 1 : 0,
    },
    {
      id: "implementationSteps" as TabType,
      icon: LightbulbIcon,
      label: tC(contentLanguage, "aiAssignmentDesigner.implementationSteps"),
      count: assignment.implementationSteps?.length || 0,
    },
    {
      id: "reflectionQuestions" as TabType,
      icon: HelpCircle,
      label: tC(contentLanguage, "aiAssignmentDesigner.reflectionQuestions"),
      count: assignment.reflectionQuestions?.length || 0,
    },
    {
      id: "gradingRubric" as TabType,
      icon: Ruler,
      label: tC(contentLanguage, "aiAssignmentDesigner.gradingRubric"),
      count: assignment.gradingRubric?.length || 0,
    },
    {
      id: "aiPromptExamples" as TabType,
      icon: MessageSquare,
      label: tC(contentLanguage, "aiAssignmentDesigner.aiPromptExamples"),
      count: assignment.aiPromptExamples?.length || 0,
    },
  ].filter((tab) => tab.count > 0);

  // Set default active tab based on available content
  useState(() => {
    if (tabs.length > 0 && !tabs.find((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  });

  // Copy example to clipboard
  const copyPrompt = (example: string, idx: number) => {
    navigator.clipboard.writeText(example).then(() => {
      // Set copied state for this specific example
      const key = `example-${idx}`;
      setCopiedPrompts((prev) => ({ ...prev, [key]: true }));

      // Reset after 2 seconds
      setTimeout(() => {
        setCopiedPrompts((prev) => ({ ...prev, [key]: false }));
      }, 2000);
    });
  };

  const renderLabel = (icon: React.ReactNode, text: string) => (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100/30 dark:bg-amber-900/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
      {icon}
      <span className="font-medium text-amber-700 dark:text-amber-300">
        {text}
      </span>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "studentInstructions":
        return assignment.studentInstructions ? (
          <div className="space-y-6">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl p-6 border border-amber-100/50 dark:border-amber-700/30 shadow-sm hover:shadow-lg transition-all duration-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 shadow-sm">
                  <ClipboardList size={20} />
                </div>
                <h4 className="font-medium text-lg text-amber-700 dark:text-amber-300">
                  {tC(
                    contentLanguage,
                    "aiAssignmentDesigner.studentInstructions",
                  )}
                </h4>
              </div>

              <div className="prose prose-amber dark:prose-invert max-w-none">
                <Markdown
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-2xl font-bold text-amber-800 dark:text-amber-300 mb-4 pb-2 border-b border-amber-200 dark:border-amber-700">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-xl font-bold text-amber-700 dark:text-amber-400 mt-6 mb-3">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-lg font-semibold text-amber-600 dark:text-amber-400 mt-5 mb-2">
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => (
                      <p className="my-3 text-gray-700 dark:text-gray-300 leading-relaxed">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="my-4 pl-6 space-y-2">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="my-4 pl-6 space-y-2 list-decimal">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="ml-2 pl-1 text-gray-700 dark:text-gray-300">
                        {children}
                      </li>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="pl-4 italic border-l-4 border-amber-300 dark:border-amber-700 my-4 text-gray-700 dark:text-gray-300">
                        {children}
                      </blockquote>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-bold text-amber-700 dark:text-amber-400">
                        {children}
                      </strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic text-amber-600 dark:text-amber-500">
                        {children}
                      </em>
                    ),
                  }}
                >
                  {assignment.studentInstructions}
                </Markdown>
              </div>
            </div>

            {/* Ethical Usage Tips */}
            <div className="p-5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
              <div className="flex items-start gap-3">
                <BookmarkIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-700 dark:text-amber-300 mb-3">
                    {tC(
                      contentLanguage,
                      "bloomsTaxonomyActivity.ethicalUsageTips",
                    )}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                      </div>
                      <p className="text-amber-700 dark:text-amber-300/90">
                        {tC(
                          contentLanguage,
                          "bloomsTaxonomyActivity.ethicTip1",
                        )}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                      </div>
                      <p className="text-amber-700 dark:text-amber-300/90">
                        {tC(
                          contentLanguage,
                          "bloomsTaxonomyActivity.ethicTip2",
                        )}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                      </div>
                      <p className="text-amber-700 dark:text-amber-300/90">
                        {tC(
                          contentLanguage,
                          "bloomsTaxonomyActivity.ethicTip3",
                        )}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                      </div>
                      <p className="text-amber-700 dark:text-amber-300/90">
                        {tC(
                          contentLanguage,
                          "bloomsTaxonomyActivity.ethicTip4",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null;

      case "implementationSteps":
        return assignment.implementationSteps &&
          assignment.implementationSteps.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-x-2 gap-y-5">
              {assignment.implementationSteps.map((step, idx) => (
                <div key={idx} className="col-span-12 flex">
                  <div className="mr-4 relative flex flex-col items-center">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500 text-white font-bold text-sm shadow-md z-10">
                      {idx + 1}
                    </div>
                    {idx <
                      (assignment.implementationSteps?.length || 0) - 1 && (
                      <div className="absolute top-8 h-full w-0.5 bg-amber-200 dark:bg-amber-800/50"></div>
                    )}
                  </div>
                  <div className="flex-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl p-5 border border-amber-100/50 dark:border-amber-700/30 shadow-sm hover:shadow-lg transition-all duration-200">
                    <Markdown className="prose dark:prose-invert prose-amber">
                      {step}
                    </Markdown>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null;

      case "reflectionQuestions":
        return assignment.reflectionQuestions &&
          assignment.reflectionQuestions.length > 0 ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {assignment.reflectionQuestions.map((question, idx) => (
                <div
                  key={idx}
                  className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl p-5 border border-amber-100/50 dark:border-amber-700/30 hover:shadow-lg transition-all duration-200 shadow-amber-100/20 h-full"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 w-8 h-8 rounded-full flex items-center justify-center font-semibold shrink-0">
                      {idx + 1}
                    </div>
                    <h5 className="font-medium text-amber-700 dark:text-amber-300">
                      {tC(contentLanguage, "aiAssignmentDesigner.question")}
                    </h5>
                  </div>
                  <div className="prose dark:prose-invert prose-sm prose-amber pl-11">
                    <Markdown>{question}</Markdown>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle
                  size={18}
                  className="text-green-600 dark:text-green-400"
                />
                <h6 className="font-medium text-green-700 dark:text-green-300">
                  {tC(
                    contentLanguage,
                    "aiAssignmentDesigner.reflectionTipsTitle",
                  )}
                </h6>
              </div>
              <p className="text-sm text-green-700/80 dark:text-green-300/80">
                {assignment.reflectionQuestions &&
                assignment.reflectionQuestions.length > 0
                  ? tC(contentLanguage, "aiAssignmentDesigner.reflectionTips")
                  : "No reflection questions available."}
              </p>
            </div>
          </div>
        ) : null;

      case "gradingRubric":
        return assignment.gradingRubric &&
          assignment.gradingRubric.length > 0 ? (
          <div className="space-y-6">
            {assignment.gradingRubric.map((rubricItem, idx) => (
              <div
                key={idx}
                className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl border border-amber-100/50 dark:border-amber-700/30 transition-all duration-200 shadow-sm hover:shadow-lg"
              >
                <div
                  className="p-4 border-b border-amber-100 dark:border-amber-800/30 cursor-pointer"
                  onClick={() => toggleRubric(idx)}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h5 className="font-semibold text-lg text-amber-700 dark:text-amber-300 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      {rubricItem.criterion}
                    </h5>
                    <div className="flex items-center gap-2">
                      <div className="bg-amber-50 dark:bg-amber-900/20 px-3 py-1 rounded-full text-amber-600 dark:text-amber-400 font-medium text-sm">
                        {tC(contentLanguage, "aiAssignmentDesigner.weights")}:{" "}
                        {rubricItem.weights}
                      </div>
                      {expandedRubrics.includes(idx) ? (
                        <ChevronUp className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                      )}
                    </div>
                  </div>
                  {rubricItem.description && (
                    <div className="mt-3 text-gray-600 dark:text-gray-300 text-sm ml-8 border-l-2 border-amber-200 dark:border-amber-800/50 pl-3">
                      <Markdown className="prose-sm">
                        {rubricItem.description}
                      </Markdown>
                    </div>
                  )}
                </div>
                <AnimatePresence initial={false}>
                  {expandedRubrics.includes(idx) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="p-4">
                        <h6 className="text-sm text-amber-500 dark:text-amber-400 mb-3 uppercase tracking-wide font-medium">
                          {tC(
                            contentLanguage,
                            "aiAssignmentDesigner.levels",
                            "Performance Levels",
                          )}
                        </h6>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                          {Object.entries(rubricItem.levels || {})
                            .sort((a, b) => {
                              // Sort numerically to ensure best level (0) comes first
                              const levelA = parseInt(a[0]);
                              const levelB = parseInt(b[0]);
                              return levelA - levelB;
                            })
                            .map(([level, description], levelIdx, arr) => {
                              // Determine color based on position (best to worst)
                              const colors = [
                                "bg-green-50/70 border-green-200/70 dark:bg-green-900/10 dark:border-green-800/30", // Best/first level
                                "bg-amber-50/70 border-amber-100/70 dark:bg-amber-900/10 dark:border-amber-800/30", // Middle level
                                "bg-red-50/70 border-red-100/70 dark:bg-red-900/10 dark:border-red-800/30", // Worst/last level
                              ];
                              const colorIndex = Math.min(
                                levelIdx,
                                colors.length - 1,
                              );
                              const levelColor = colors[colorIndex];

                              const indicatorColors = [
                                "bg-green-500",
                                "bg-amber-500",
                                "bg-red-500",
                              ];
                              const indicatorColor =
                                indicatorColors[colorIndex];

                              return (
                                <div
                                  key={levelIdx}
                                  className={`p-3 rounded-lg border hover:shadow-sm transition-all duration-200 ${levelColor}`}
                                >
                                  <div className="flex items-center gap-2 pb-1.5 mb-1.5 border-b border-amber-100 dark:border-amber-800/30">
                                    <div
                                      className={`w-3 h-3 rounded-full ${indicatorColor}`}
                                    ></div>
                                    <div className="flex justify-between w-full">
                                      <h6 className="font-medium text-amber-600 dark:text-amber-400 text-sm">
                                        {levelIdx === 0 &&
                                          tC(
                                            contentLanguage,
                                            "aiAssignmentDesigner.performanceLevels.excellent",
                                          )}
                                        {levelIdx === 1 &&
                                          tC(
                                            contentLanguage,
                                            "aiAssignmentDesigner.performanceLevels.satisfactory",
                                          )}
                                        {levelIdx === 2 &&
                                          tC(
                                            contentLanguage,
                                            "aiAssignmentDesigner.performanceLevels.needsImprovement",
                                          )}
                                      </h6>
                                    </div>
                                  </div>
                                  <div className="prose-sm text-gray-700 dark:text-gray-300">
                                    <Markdown className="prose-sm">
                                      {description}
                                    </Markdown>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        ) : null;

      case "aiPromptExamples":
        return assignment.aiPromptExamples &&
          assignment.aiPromptExamples.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 shadow-sm">
                <MessageSquare size={20} />
              </div>
              <h4 className="font-medium text-amber-700 dark:text-amber-300">
                {tC(contentLanguage, "aiAssignmentDesigner.aiPromptExamples")}
              </h4>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {assignment.aiPromptExamples.map((example, idx) => {
                const promptKey = `example-${idx}`;
                const isCopied = copiedPrompts[promptKey];

                return (
                  <div
                    key={idx}
                    className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl p-5 border border-amber-100/50 dark:border-amber-700/30 hover:shadow-lg transition-all duration-200 shadow-amber-100/20"
                  >
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="font-medium text-amber-700 dark:text-amber-300 flex items-center gap-2">
                        <MessageSquare size={16} className="text-amber-500" />
                        {tC(
                          contentLanguage,
                          "aiAssignmentDesigner.example",
                        )}{" "}
                        {idx + 1}
                      </div>
                    </div>
                    <div className="relative bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                      <div className="absolute top-2 right-2 z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyPrompt(example, idx);
                          }}
                          className="flex items-center justify-center p-1.5 rounded-md bg-gray-200/70 dark:bg-gray-700/70 backdrop-blur-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          title={isCopied ? "Copied!" : "Copy to clipboard"}
                        >
                          {isCopied ? (
                            <Check className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                          ) : (
                            <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                      <div className="p-4 font-mono text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {example}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;

      default:
        return null;
    }
  };
  const isAuthor = useIsAuthor(assignment.authorId);
  return (
    <div
      data-testid="ai-assignment-card"
      className="min-h-screen bg-gradient-to-b from-amber-50/30 to-white dark:from-gray-900/50 dark:to-gray-900"
    >
      {isAuthor && (
        <MascotBubble
          toolsQueryKey={getGetUserAiAssignmentsQueryKey()}
          toolQueryKey={getGetAiAssignmentQueryKey(assignment.id)}
          createChatParams={{
            teacherToolId: assignment.id,
            toolType: "AI_ASSIGNMENT",
          }}
        />
      )}
      <div className="container max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <CardImage
          createdAt={assignment.createdAt}
          imageUrl={assignment.imageUrl}
          videoUrl={assignment.videoUrl}
          courseName={assignment.courseName}
          gradeLevel={assignment.gradeLevel}
          topic={assignment.title}
          theme="amber"
          isLoading={isImageLoading}
          contentLanguage={contentLanguage}
        />

        <div className="flex justify-end  mt-2 mb-6 gap-3">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              rounded="xl"
              className="shadow-md hover:shadow-lg transition-all duration-200"
            >
              <RefreshCw size={18} className="mr-1" />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            pdfTitle={assignment.title ?? ""}
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            document={
              <AiAssignmentCardPDF
                customizationSettings={customizationSettings}
                assignment={{ ...assignment, imageUrl: finalImageUrl || "" }}
              />
            }
            btnTheme="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
          />
        </div>

        {tabs.length > 0 && (
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-amber-100/30 dark:border-amber-700/30 shadow-amber-500/5 mb-6 overflow-hidden relative">
            <div className="sticky -top-6 rounded-t-2xl z-10 p-4 bg-amber-50/90 dark:bg-gray-900/90">
              <div className="flex flex-wrap justify-center items-center gap-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        scrollRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 
                        ${
                          isActive
                            ? "bg-gradient-to-r from-amber-400 to-amber-500 text-white shadow-lg shadow-amber-500/30"
                            : "bg-white/50 dark:bg-gray-800/50 text-amber-700 dark:text-amber-300 hover:bg-amber-50/80 hover:shadow-md shadow-sm"
                        }`}
                    >
                      <Icon size={18} className="shrink-0" />
                      <span className="truncate">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="p-6 relative">
              <div ref={scrollRef} className="absolute -top-28 -z-10" />
              {renderTabContent()}
            </div>
            {assignment.status === "REGENERATING" && <RegeneratingOverlay />}
          </div>
        )}
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <AiAssignmentCardPDF
            customizationSettings={customizationSettings}
            assignment={{ ...assignment, imageUrl: finalImageUrl || "" }}
          />
        }
      />
    </div>
  );
};

export default AiAssignmentCard;
